import type { LocalGraphNodes } from './CTGStep';

/**
 * Subject of the email step that runs immediately before `nodeId`, or null when this is the first
 * email step. Drives the "continue previous thread" toggle: it only shows when an earlier email
 * step exists, and the returned subject is what a thread-continuing step inherits.
 *
 * Sequential campaign steps aren't a parent→child chain: a delay group's runnable step and the
 * continuation-delay that leads to the next group are SIBLINGS under a shared delay. So the previous
 * email is either an email ancestor (a rarer email→delay chain) or an email sibling of one of the
 * delay ancestors we walk up through.
 */
export function findPreviousEmailSubject(nodes: LocalGraphNodes, nodeId: string): string | null {
  const seen = new Set<string>();
  let childId: string | null = nodeId;
  while (childId != null && !seen.has(childId)) {
    seen.add(childId);
    const parentId: string | null = nodes[childId]?.parent_id ?? null;
    if (parentId == null) break;
    const parent = nodes[parentId];
    if (parent == null) break;
    // An email ancestor (email → delay → … chain) is the previous email directly. A blank subject
    // means there's no thread subject to inherit (matches the server), so treat it as no match.
    if (parent.type === 'email') return blankToNull(parent.subject);
    // Otherwise the group's email step is a sibling of the continuation-delay branch we came up.
    const emailSibling = Object.values(nodes).find(
      (n) => n.parent_id === parentId && n.type === 'email' && n.id !== childId,
    );
    if (emailSibling != null) return blankToNull(emailSibling.subject);
    childId = parentId;
  }
  return null;
}

function blankToNull(subject: string | null): string | null {
  return subject != null && subject.trim().length > 0 ? subject : null;
}
